import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Tabs, Spin, Modal } from 'antd';
import { CloseOutlined, EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import MyCasePanel from './components/MyCasePanel';
import AllCasePanel from './components/AllCasePanel';
import { getTimeAgo, getDetailText } from 'pages/dashboard/TextVisitPage/utils';
import PreviewDrawer from './components/PreviewDrawer';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import TextVisitLandingPage from '../LandingPage';
import WarningTips from 'components/WarningTips';
import useGetRecommendCase from 'hooks/textVisit/useGetRecommendCase';
import useGetEligibleStatus from 'hooks/textVisit/useGetEligibleStatus';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { dismissRecommendCase, requestAccess, acceptCase } from 'api/textVisit';
import { CaseListItem } from 'types/textVisit';
import useGetAllCaseCount from 'hooks/textVisit/useGetAllCaseCount';
import DefaultIcon from 'assets/conditionIcon/default-condition.png';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import useCurrentUser from 'hooks/useCurrentUser';
import { canShowGroupPracticeView } from 'utils/common';
import { EFeatureType } from 'types/provider';
import homeStore from 'store/Home';
import PracticeView from './components/PracticeView';

const Dashboard = () => {
    const [activeTab, setActiveTab] = useState<'my' | 'all'>('all');
    const [selectedCase, setSelectedCase] = useState<CaseListItem>();
    const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
    const [showTextVisitUnSubmittedDrawer, setShowTextVisitUnSubmittedDrawer] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showLandingModal, setShowLandingModal] = useState(false);
    const [nextCaseUuid, setNextCaseUuid] = useState<string>();

    const [recommendedCase, loading, refetchRecommendCase] = useGetRecommendCase();
    const [allCaseCount, _, refetchAllCaseCount] = useGetAllCaseCount();
    const [settings] = useSettings();
    const stateList = settings[EnumFields.STATE] || [];
    const allCasePanelRef = useRef<{ fetchData: () => void }>(null);
    const myCasePanelRef = useRef<{ fetchData: () => void }>(null);

    const [acceptCaseLoading, setAcceptCaseLoading] = useState(false);
    const [requestAccessLoading, setRequestAccessLoading] = useState(false);

    const [eligibleStatus, __, fetchEligibleStatus] = useGetEligibleStatus();

    const providerEligible = eligibleStatus?.eligible;
    const isApplyForTextVisit = eligibleStatus?.status === 'PENDING';

    // Practice view (Owner / PM / Assistant) — only for PPA providers with the Text Visit feature
    const [currentUser] = useCurrentUser();
    const [getHomeStore] = homeStore.useStore();
    const homeInfo = getHomeStore('data');
    const isPPAProvider = !!homeInfo?.ppaProvider;
    const hasTextVisitFeature = !!homeInfo?.features?.includes(EFeatureType.TEXT_VISIT);
    const showPracticeView = canShowGroupPracticeView(currentUser) && isPPAProvider && hasTextVisitFeature;
    const [activeView, setActiveView] = useState<'practice' | 'my'>('practice');

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const items = [
        { label: `My cases (${allCaseCount.totalCount})`, key: 'my' },
        { label: 'All cases', key: 'all' },
    ];

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && items.some((item) => item.key === tab)) {
            setActiveTab(tab as 'my' | 'all');
        }
    }, [searchParams]);

    const handleAcceptCase = async (caseId: number, isRecommendCase = false) => {
        if (!providerEligible) {
            setShowApproveModal(true);
            return;
        }
        try {
            setAcceptCaseLoading(true);
            const res = await acceptCase(caseId);
            if (!res.error && res.data?.data) {
                setShowPreviewDrawer(false);
                setShowTextVisitUnSubmittedDrawer(true);
                if (isRecommendCase) {
                    await refetchRecommendCase();
                } else {
                    allCasePanelRef.current?.fetchData();
                }
                refetchAllCaseCount();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setAcceptCaseLoading(false);
        }
    };

    const handleViewDetails = (caseData: CaseListItem) => {
        setSelectedCase(caseData);
        setShowPreviewDrawer(true);
    };

    const handleRequestAccess = (caseData: CaseListItem) => {
        setSelectedCase(caseData);
        setShowApproveModal(true);
    };

    const handleResumeCase = (caseData: CaseListItem) => {
        setSelectedCase(caseData);
        setShowTextVisitUnSubmittedDrawer(true);
    };

    const dismissRecommendedCase = async (caseId: number) => {
        try {
            await dismissRecommendCase(caseId);
            await refetchRecommendCase();
        } catch (error) {
            console.error(error);
        }
    };

    const requestForAccess = async () => {
        try {
            setRequestAccessLoading(true);
            await requestAccess(selectedCase!.id);
            setShowApproveModal(false);
            setShowPreviewDrawer(false);
            fetchEligibleStatus();
        } catch (error) {
            console.error(error);
        } finally {
            setRequestAccessLoading(false);
        }
    };

    const renderRecommendedCase = () => {
        if (!recommendedCase) return null;

        return (
            <Spin spinning={loading}>
                <div className={s.recommendedCase}>
                    <div className={s.recommendedHeader}>
                        <div className={s.recommendedLeft}>
                            <span className={s.waitingBadge}>Patient is waiting for care</span>
                            <div className={s.timeBadge}>
                                <ClockCircleOutlined />
                                <span>{getTimeAgo(recommendedCase.patientSubmitAt || '').text}</span>
                            </div>
                        </div>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => dismissRecommendedCase(recommendedCase.id)}
                            className={s.closeBtn}
                        />
                    </div>

                    <div className="flex justify-between">
                        <div className={s.caseContent}>
                            <div className={s.caseIcon}>
                                <span className={recommendedCase.icon ? s.caseIconImage : undefined}>
                                    <img src={recommendedCase.icon || DefaultIcon} alt="img" width={24} height={24} />
                                </span>
                                <span>{recommendedCase.assessmentName || recommendedCase.conditions}</span>
                            </div>

                            <p className={s.caseDescription}>{recommendedCase.aiSummary}</p>

                            <div className={s.caseLocation}>
                                <EnvironmentOutlined />
                                <span>{getDetailText(recommendedCase, stateList)}</span>
                            </div>
                        </div>

                        <div className={s.caseActions}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setSelectedCase(recommendedCase);
                                    handleAcceptCase(recommendedCase.id, true);
                                }}
                                loading={acceptCaseLoading}
                            >
                                Accept case
                            </Button>
                            <Button onClick={() => handleViewDetails(recommendedCase)} style={{ background: '#fff' }}>
                                View details
                            </Button>
                        </div>
                    </div>
                </div>
            </Spin>
        );
    };

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className="flex justify-between">
                    <h1 className={s.title}>Text visits</h1>
                    <Button onClick={() => setShowLandingModal(true)} style={{ background: '#fff' }}>
                        Learn more
                    </Button>
                </div>
                <p className={s.subtitle}>
                    View and pick cases for text visits matching your license and specialty—no video calls or in-person
                    visit, just intake review and messaging.
                </p>
            </div>
            {isApplyForTextVisit && (
                <div className="mb-4">
                    <WarningTips title="Your text visits access is under review. You’ll be able to accept cases once you’re approved." />
                </div>
            )}

            {showPracticeView && (
                <Tabs
                    activeKey={activeView}
                    onChange={(key) => setActiveView(key as 'practice' | 'my')}
                    items={[
                        { label: 'Practice view', key: 'practice' },
                        { label: 'My cases', key: 'my' },
                    ]}
                />
            )}

            {showPracticeView && activeView === 'practice' ? (
                <PracticeView />
            ) : (
                <>
                    {recommendedCase && renderRecommendedCase()}

                    <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'my' | 'all')} items={items} />

                    {activeTab === 'my' && <MyCasePanel ref={myCasePanelRef} onResumeCase={handleResumeCase} />}

                    {activeTab === 'all' && (
                        <AllCasePanel
                            onViewCase={handleViewDetails}
                            onRequestAccess={handleRequestAccess}
                            ref={allCasePanelRef}
                            providerEligible={providerEligible}
                        />
                    )}
                </>
            )}

            <PreviewDrawer
                show={showPreviewDrawer}
                onClose={() => setShowPreviewDrawer(false)}
                onAccept={(caseId) => {
                    handleAcceptCase(caseId);
                }}
                textVisitUuid={selectedCase?.textVisitUuid || nextCaseUuid}
                acceptLoading={acceptCaseLoading}
                isProviderEligible={providerEligible}
                shouldMaskData={activeTab === 'all'}
            />
            <TextVisitUnSubmittedDrawer
                show={showTextVisitUnSubmittedDrawer}
                onClose={() => setShowTextVisitUnSubmittedDrawer(false)}
                onNext={(data) => {
                    const { nextTextVisitUuid, inProgress } = data;
                    // console.log('onNext', nextTextVisitUuid, inProgress);
                    if (nextTextVisitUuid) {
                        setSelectedCase(undefined);
                        setNextCaseUuid(nextTextVisitUuid);
                        if (inProgress) {
                            setShowTextVisitUnSubmittedDrawer(true);
                        } else {
                            setShowPreviewDrawer(true);
                        }
                    }
                }}
                onSave={() => {
                    myCasePanelRef.current?.fetchData();
                }}
                textVisitUuid={selectedCase?.textVisitUuid || nextCaseUuid}
            />
            <Modal
                open={showApproveModal}
                onCancel={() => setShowApproveModal(false)}
                title="Get approved to take text visits"
                okText="Request access"
                cancelText="Cancel"
                onOk={requestForAccess}
                okButtonProps={{ loading: requestAccessLoading }}
            >
                <div>
                    Before you can accept this case, we need to verify that you’re eligible for text visits. Submit a
                    quick request and our operations team will review your credentials. Once approved, you’ll get
                    notified and can start taking text visits cases right away.
                </div>
            </Modal>
            <Modal
                open={showLandingModal}
                onCancel={() => setShowLandingModal(false)}
                footer={null}
                width="84%"
                title="Learn more about Text Visits"
                className={commonS.modalFixHeightWithoutPaddingWrap}
                centered
            >
                <TextVisitLandingPage isModal />
            </Modal>
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_TEXT_VISIT} defaultShow={false} makeFix={false} />
            </div>
        </div>
    );
};

export default Dashboard;
